(() => {
  if (window.__charryCollectionReturnInitialized) return;
  window.__charryCollectionReturnInitialized = true;

  const stateKey = 'charryCollectionReturn';
  const gridSelector = '#CollectionProductGrid';
  const currentLocation = () => window.location.pathname + window.location.search;
  const productCards = () => Array.from(document.querySelectorAll(`${gridSelector} .productListing > .product`));
  const pageFromUrl = () => {
    const page = Number(new URLSearchParams(window.location.search).get('page'));
    return Number.isSafeInteger(page) && page > 0 ? page : 1;
  };
  // This deferred script runs after the first server-rendered page is parsed.
  let firstPageSize = productCards().length;
  let firstPage = pageFromUrl();
  let restorationAborted = false;
  let restorationFrame = 0;
  let listeningForInput = false;
  const collection = document.querySelector(`${gridSelector} .collection`);
  if (collection) {
    let listing = collection.querySelector('.productListing');
    new MutationObserver(() => {
      const nextListing = collection.querySelector('.productListing');
      if (nextListing === listing) return;
      listing = nextListing;
      firstPageSize = productCards().length;
      firstPage = pageFromUrl();
    }).observe(collection, { childList: true });
  }
  const hasProductLink = (card, product) => Array.from(card?.querySelectorAll('a[href]') || [])
    .some((link) => link.pathname === product);

  window.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    const card = link?.closest('.product');
    const listing = card?.closest(`${gridSelector} .productListing`);
    if (!listing || link.target === '_blank' || link.hasAttribute('download')) return;

    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin || !destination.pathname.startsWith('/products/')) return;

    const cards = productCards();
    const cardIndex = cards.indexOf(card);
    if (cardIndex < 0 || firstPageSize < 1) return;

    const pageNumber = firstPage + Math.floor(cardIndex / firstPageSize);
    const pageIndex = cardIndex % firstPageSize;
    const returnUrl = new URL(window.location.href);
    if (pageNumber === 1) returnUrl.searchParams.delete('page');
    else returnUrl.searchParams.set('page', pageNumber);
    const returnLocation = returnUrl.pathname + returnUrl.search;

    try {
      const saved = {
        location: returnLocation,
        product: destination.pathname,
        count: cards.length,
        cardIndex,
        pageIndex,
        pageNumber,
        pageSize: firstPageSize,
        cardTop: card.getBoundingClientRect().top,
        scrollY: window.scrollY,
        savedAt: Date.now()
      };
      restorationAborted = false;
      history.scrollRestoration = 'manual';
      const state = history.state && typeof history.state === 'object' ? history.state : {};
      // Each visit keeps its own product, even when the same collection is opened again.
      history.replaceState({ ...state, [stateKey]: saved }, '', returnUrl);
    } catch (error) {
      history.scrollRestoration = 'auto';
      return;
    }
  });

  const findSavedCard = (saved) => {
    const cards = productCards();
    // BFCache keeps the full list; a fresh document contains just the saved page.
    if (hasProductLink(cards[saved.cardIndex], saved.product)) return cards[saved.cardIndex];
    if (hasProductLink(cards[saved.pageIndex], saved.product)) return cards[saved.pageIndex];

    let closest = null;
    cards.forEach((card, index) => {
      if (hasProductLink(card, saved.product) && (!closest ||
          Math.abs(index - saved.pageIndex) < Math.abs(closest.index - saved.pageIndex))) {
        closest = { card, index };
      }
    });
    if (closest) return closest.card;
    // Products can disappear or be reordered while the shopper views the PDP.
    return cards[cards.length >= saved.count ? saved.cardIndex : saved.pageIndex] || cards.at(-1) || null;
  };

  const inputEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
  const stopRestoration = () => {
    restorationAborted = true;
    cancelAnimationFrame(restorationFrame);
    history.scrollRestoration = 'auto';
    inputEvents.forEach((name) => window.removeEventListener(name, stopRestoration, true));
    listeningForInput = false;
  };

  const restorePosition = (saved) => {
    const card = findSavedCard(saved);
    if (!card) return;

    cancelAnimationFrame(restorationFrame);
    const deadline = performance.now() + 1800;
    let lastCheck = -Infinity;
    let focusChecked = false;
    const reanchor = (now) => {
      if (restorationAborted || !card.isConnected || currentLocation() !== saved.location) return;
      if (now - lastCheck >= 100) {
        lastCheck = now;
        const offset = card.getBoundingClientRect().top - saved.cardTop;
        if (Math.abs(offset) > 2) window.scrollTo({ top: window.scrollY + offset, behavior: 'instant' });

        if (!focusChecked) {
          focusChecked = true;
          const link = Array.from(card.querySelectorAll('a[href]'))
            .find((candidate) => candidate.pathname === saved.product);
          const visibleModal = Array.from(document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"]'))
            .some((modal) => modal.getClientRects().length > 0);
          if (link && document.activeElement === document.body && !visibleModal) link.focus({ preventScroll: true });
        }
      }
      if (now < deadline) restorationFrame = requestAnimationFrame(reanchor);
    };
    restorationFrame = requestAnimationFrame(reanchor);
  };

  const restoreOnReturn = () => {
    const saved = history.state?.[stateKey];

    if (!saved || saved.location !== currentLocation() ||
        !Number.isSafeInteger(saved.count) || saved.count < 1 ||
        !Number.isSafeInteger(saved.cardIndex) || saved.cardIndex < 0 || saved.cardIndex >= saved.count ||
        !Number.isSafeInteger(saved.pageIndex) || saved.pageIndex < 0 ||
        !Number.isSafeInteger(saved.pageSize) || saved.pageSize < 1 || saved.pageIndex >= saved.pageSize ||
        !Number.isSafeInteger(saved.pageNumber) || saved.pageNumber < 1 ||
        !Number.isFinite(saved.cardTop) || !Number.isFinite(saved.scrollY) ||
        typeof saved.product !== 'string' || !saved.product.startsWith('/products/') ||
        !Number.isFinite(saved.savedAt) || saved.savedAt > Date.now() ||
        Date.now() - saved.savedAt > 30 * 60 * 1000) return;

    if (!document.querySelector(gridSelector) || !findSavedCard(saved) || restorationAborted) return;
    history.scrollRestoration = 'manual';
    if (!listeningForInput) {
      inputEvents.forEach((name) => window.addEventListener(name, stopRestoration, { capture: true, passive: true }));
      listeningForInput = true;
    }
    restorePosition(saved);
  };

  const navigation = performance.getEntriesByType('navigation')[0];
  // A deferred script can restore a cold Back before late app assets finish loading.
  if (navigation?.type === 'back_forward') restoreOnReturn();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted || navigation?.type === 'back_forward') restoreOnReturn();
  });
})();
